import { Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import MuiAccordion from '@mui/material/Accordion'
import MuiAccordionSummary from '@mui/material/AccordionSummary'
import MuiAccordionDetails from '@mui/material/AccordionDetails'
import { useState } from 'react'
import accordianArrow from '../../assets/icons/accordion.svg'
import pdfIcon from '../../assets/icons/pdf-icon.svg'
import CourseVideo from '../course-video/CourseVideo'
import { capitalizeFirstLetter, formatTime } from '../../utility/genricFunction'


const Accordion = styled((props) => <MuiAccordion disableGutters elevation={0} square {...props} />)(() => ({
  'backgroundColor': '#f4f5fb',
  'padding': 0,
  'border': `1px solid #E2EAED`,
  '&:not(:last-child)': {
    borderBottom: 0,
  },
  '&::before': {
    display: 'none',
  },
  '& .MuiAccordionDetails-root': {
    padding: 0,
    borderTop: 'none',
  },
  '& .MuiPaper-root MuiPaper-elevation': {
    padding: '0 21px',
    minWidth: '100%',
    backgroundColor: 'red',
  },
}))

const AccordionSummary = styled((props) => (
  <MuiAccordionSummary expandIcon={<img src={accordianArrow} alt='' />} {...props} />
))(({ theme }) => ({
  'flexDirection': 'row-reverse',
  '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
    transform: 'rotate(-180deg)',
  },
  '& .MuiAccordionSummary-content': {
    marginLeft: theme.spacing(1),
    justifyContent: 'space-between',
  },
}))

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  padding: theme.spacing(2),
  borderTop: '1px solid rgba(0, 0, 0, .125)',
}))

function AccordianCourseDetail({ courseData }) {
  const [parentExpanded, setParentExpanded] = useState('panel0')
  const [childExpanded, setChildExpanded] = useState('panel0-0')
  const [playingVideo, setPlayingVideo] = useState(null)

  const handleParentChange = (panel) => (event, newExpanded) => {
    setParentExpanded(newExpanded ? panel : false)
    setChildExpanded(newExpanded ? '' : false)
  }

  const handleChildChange = (panel) => (event, newExpanded) => {
    setChildExpanded(newExpanded ? panel : false)
  }

  const totalVideoLength = (topics) => {
    return topics.reduce((acc, topic) => {
      const validVideos = topic.courseVideoUrls.filter((video) => video.shortId !== null)
      const length = validVideos.length
      return acc + length
    }, 0)
  }

  return courseData?.modules?.map((module, moduleIndex) => {
    const totalPdfs = module?.topics?.reduce((acc, topic) => acc + topic?.courseDocumentUrls?.length, 0)

    return (
      <Accordion
        key={moduleIndex}
        expanded={parentExpanded === `panel${moduleIndex}`}
        onChange={handleParentChange(`panel${moduleIndex}`)}
      >
        <AccordionSummary aria-controls={`panel${moduleIndex}d-content`} id={`panel${moduleIndex}d-header`}>
          <Typography className='accordion-header'>{capitalizeFirstLetter(module?.courseModuleName)}</Typography>
          <Typography className='accordion-desc'>
            {module.topics.length} Topics • {totalVideoLength(module.topics)} {totalVideoLength(module.topics) > 1 ? 'Videos' : 'Video'} •{' '}
            {module?.documentCount} PDFs
          </Typography>
        </AccordionSummary>

        <AccordionDetails className='accordion-detail'>
          {module.topics.map((topic, topicIndex) => (
            <Accordion
              key={topicIndex}
              expanded={childExpanded === `panel${moduleIndex}-${topicIndex}`}
              onChange={handleChildChange(`panel${moduleIndex}-${topicIndex}`)}
              className='accordion'
            >
              <AccordionSummary
                aria-controls={`panel${moduleIndex}-${topicIndex}d-content`}
                id={`panel${moduleIndex}-${topicIndex}d-header`}
              >
                <Typography className='accordion-header'>
                  {capitalizeFirstLetter(topic?.courseModuleTopicName)}
                </Typography>
                <Typography className='accordion-desc'>
                  {topic?.courseVideoUrls?.filter((video) => video.shortId !== null).length}{' '}
                  {topic?.courseVideoUrls?.filter((video) => video.shortId !== null).length > 1 ? 'Videos' : 'Video'} •{' '}
                  {topic?.courseDocumentUrls?.length} PDF
                </Typography>
              </AccordionSummary>

              <AccordionDetails className='accordion-detail'>
                <div className='topic-wrapper'>
                  {topic?.courseVideoUrls?.map(
                    (video, videoIndex) =>
                      video.shortId && (
                        <div key={videoIndex} className='video-div'>
                          <CourseVideo
                            isExpired={video?.isExpired}
                            video={video?.url}
                            shortId={video?.shortId}
                            playingVideo={playingVideo}
                            setPlayingVideo={setPlayingVideo}
                            isAccessible={video?.isAccessible}
                          />
                          <p className='video-detail'>{formatTime(video.duration)} min</p>
                        </div>
                      ),
                  )}
                  {topic?.courseDocumentUrls?.map((file, fileIndex) => {
                    const fileExtension = file?.originalPath?.slice(-3).toLowerCase();

                    const handleDownload = (file) => {
                      if (fileExtension === 'zip') {
                        const link = document.createElement('a');
                        // eslint-disable-next-line no-undef
                        link.href = `${process.env.REACT_APP_LIVE_URL}/course/module/topic/upload-doc/${file.shortId}`;
                        link.download = file?.originalPath || 'file.zip'; // Optional: Specify a download filename
                        console.log("link", link)
                        link.click();
                      }
                    };

                    return (
                      (fileExtension === 'pdf' || fileExtension === 'zip') && (
                        <div key={fileIndex} className='content-div'>
                          <div className='file-div'>
                            <img
                              src={pdfIcon}
                              alt={fileExtension === 'pdf' ? 'PDF icon' : 'ZIP icon'}
                            />
                            {fileExtension === 'zip' ? (
                              <button
                                className='download-btn'
                                onClick={() => handleDownload(file)}
                              >
                                {file?.originalPath?.slice(0, -4)}
                              </button>
                            ) : (
                              <a
                                // eslint-disable-next-line no-undef
                                href={`${process.env.REACT_APP_LIVE_URL}/course/module/topic/upload-doc/${file.shortId}`}
                                target='_blank'
                                rel='noopener noreferrer'
                              >
                                {file?.originalPath?.slice(0, -4)}
                              </a>
                            )}
                          </div>
                          {fileExtension === 'pdf' ? (
                            <span>{file?.pages} pages</span>
                          ) : (
                            <span>{file?.originalPath?.slice(-3).toUpperCase()} file</span>
                          )}
                        </div>
                      )
                    );
                  })}



                </div>
              </AccordionDetails>
            </Accordion>
          ))}
        </AccordionDetails>
      </Accordion>
    )
  })
}

export default AccordianCourseDetail
